import React, { FC, useEffect, useState } from "react";

import log from "loglevel";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { Button, Checkbox, FormControlLabel, InputAdornment } from "@mui/material";

import { EmailIcon } from "@/assets/icons";
import { OrSeparator, TextInput } from "@/components/atoms";
import { TermsOfUseDialog } from "@/components/organisms";
import * as userApi from "@/shared/apis/userApi";
import { UserState } from "@/shared/domain";
import { useAuth, useNavigation } from "@/shared/hooks";

import { RegisterFormType } from "../../../Register/formType";
import { getSignUpObject } from "../../../Register/utils";
import { StyledUnknownEmailContainer } from "./styled";

type ItsmeUnknownEmailProps = {
    onItsmeLink: () => void;
    onManualRegister: () => void;
};

const ItsmeUnknownEmail: FC<ItsmeUnknownEmailProps> = ({ onItsmeLink, onManualRegister }) => {
    const { t, i18n } = useTranslation();
    const { prepare } = useAuth();
    const { navToRoot } = useNavigation();
    const [termsOpen, setTermsOpen] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const { control, setValue, trigger, getValues } = useFormContext<RegisterFormType>();

    useEffect(() => {
        prepare(UserState.UNKNOWN);
        setValue("email", "");
    }, []);

    const { mutate: itsmeSignupInit, isLoading: isLoadingItsmeSignupInit } = useMutation(userApi.itsmeSignupInit, {
        onSuccess: () => {
            log.info("itsme link email success");
            onItsmeLink();
        },
    });

    const handleEmailVerify = async () => {
        const formValues = getValues();
        try {
            const signUpObj = getSignUpObject(formValues, i18n.language);
            itsmeSignupInit({ body: signUpObj, params: { itsMeUid: formValues.profileItsMe.itsmeId } });
        } catch (e) {
            log.warn("email verify failed", e.message);
            navToRoot();
            throw e;
        }
    };

    const handleClickLinkItsmeEmail = async () => {
        setValue("linkItsMeExistingEmail", true);
        if (await trigger()) await handleEmailVerify();
    };

    const handleManualRegister = () => {
        const itsMeEmail = getValues("profileItsMe.email");
        setValue("linkItsMeExistingEmail", false);
        setValue("email", itsMeEmail);
        onManualRegister();
    };

    return (
        <>
            {termsOpen && <TermsOfUseDialog onClose={() => setTermsOpen(false)} />}
            <StyledUnknownEmailContainer data-testid="register-step-itsme-unknown">
                <div className="container">
                    <div className="container-header">
                        <span className="title">{t("ItsMe.Unknown")}</span>
                        <span className="message">{t("ItsMe.WarningMessage")}</span>
                        <span className="message">{t("ItsMe.FollowProcedure")}</span>
                    </div>
                    <div className="container-body">
                        <span className="blue-text">{t("ItsMe.MyBrokerAccount")}</span>
                        <TextInput
                            control={control}
                            name="email"
                            autoComplete="email"
                            label={t("ItsMe.EmailAddress")}
                            LabelProps={{ className: "grey-text" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button type="submit" fullWidth className="mt16" onClick={handleClickLinkItsmeEmail}>
                            {t("ItsMe.LinkToItsme")}
                        </Button>
                        <OrSeparator />
                        <span className="blue-text">{t("ItsMe.WithoutMyBrokerAccount")}</span>
                        <FormControlLabel
                            name="terms"
                            control={<Checkbox color="primary" onClick={() => setTermsAccepted(!termsAccepted)} />}
                            label={
                                <p className="form-label-checkbox">
                                    {t("Registration.DefinePassword.AcceptThe")}{" "}
                                    <span
                                        className="hyperlink "
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setTermsOpen(true);
                                        }}
                                    >
                                        {t("Registration.DefinePassword.Terms")}
                                    </span>
                                </p>
                            }
                            labelPlacement="end"
                        />
                        <Button
                            disabled={!termsAccepted || isLoadingItsmeSignupInit}
                            data-testid="button_register"
                            onClick={handleManualRegister}
                            fullWidth
                        >
                            {t("ItsMe.Register.Register")}
                        </Button>
                    </div>
                </div>
            </StyledUnknownEmailContainer>
        </>
    );
};

export default ItsmeUnknownEmail;
